import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import type { City } from "../types/city";
import { apiService } from "../services/api";
import { isTimestampWithinWindow } from "../utils/helpers";
type CityQueryParams = Parameters<typeof apiService.getCities>[0];

interface CityState {
  // Data
  cities: City[];
  selectedCity: string | null;

  // Pagination metadata
  totalCount: number;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;

  // Loading states
  loading: boolean;
  isRefreshing: boolean;
  hasLoaded: boolean;
  lastFetchedAt: string | null;

  // Actions
  fetchCities: (params?: Record<string, unknown>) => Promise<void>;
  loadIfNeeded: () => Promise<void>;
  selectCity: (cityId: string | null) => void;
  addCity: (city: Omit<City, "created_at" | "updated_at">) => Promise<void>;
  updateCity: (
    cityId: string,
    updates: Partial<Pick<City, "city_name" | "country_code">>,
  ) => Promise<void>;
  removeCity: (cityId: string) => Promise<void>;
  refreshCities: () => Promise<void>;
}

export const useCityStore = create<CityState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        cities: [],
        selectedCity: null,
        totalCount: 0,
        currentPage: 1,
        totalPages: 1,
        itemsPerPage: 20,
        loading: false,
        isRefreshing: false,
        hasLoaded: false,
        lastFetchedAt: null,

        // Actions
        fetchCities: async (params = { page: 1, limit: 20 }) => {
          set({ loading: true });
          try {
            const response = (await apiService.getCities(
              params as CityQueryParams,
            )) as {
              cities: City[];
              pagination?: {
                total: number;
                page: number;
                limit: number;
                totalPages: number;
              };
            };

            const pagination = response.pagination || {
              total: response.cities.length,
              page: 1,
              limit: response.cities.length,
              totalPages: 1,
            };

            set({
              cities: response.cities,
              totalCount: pagination.total,
              currentPage: pagination.page,
              totalPages: pagination.totalPages,
              itemsPerPage: pagination.limit,
              hasLoaded: true,
              lastFetchedAt: new Date().toISOString(),
              loading: false,
            });
          } catch (error) {
            console.error("Error fetching cities:", error);
            set({ loading: false });
          }
        },

        loadIfNeeded: async () => {
          const {
            hasLoaded,
            cities,
            isRefreshing,
            loading,
            lastFetchedAt,
            refreshCities,
          } = get();
          if (isRefreshing || loading) {
            return;
          }
          if (
            hasLoaded &&
            cities.length > 0 &&
            isTimestampWithinWindow(lastFetchedAt, 60 * 60 * 1000, Date.now())
          ) {
            return;
          }
          await refreshCities();
        },

        selectCity: (cityId) => set({ selectedCity: cityId }),

        addCity: async (city) => {
          try {
            const newCity = (await apiService.createCity(city)) as City;
            const { cities } = get();
            set({
              cities: [newCity, ...cities],
              totalCount: cities.length + 1,
            });
          } catch (error) {
            console.error("Error adding city:", error);
            throw error;
          }
        },

        updateCity: async (cityId, updates) => {
          try {
            const updatedCity = (await apiService.updateCity(
              cityId,
              updates,
            )) as City;
            const { cities } = get();
            const updatedCities = cities.map((city) =>
              city.city_id === cityId ? { ...city, ...updatedCity } : city,
            );
            set({ cities: updatedCities });
          } catch (error) {
            console.error("Error updating city:", error);
            throw error;
          }
        },

        removeCity: async (cityId) => {
          try {
            await apiService.deleteCity(cityId);
            const { cities } = get();
            const filteredCities = cities.filter(
              (city) => city.city_id !== cityId,
            );
            set({ cities: filteredCities, totalCount: filteredCities.length });
          } catch (error) {
            console.error("Error removing city:", error);
            throw error;
          }
        },

        refreshCities: async () => {
          set({ loading: true, isRefreshing: true });
          try {
            const response = (await apiService.getAllCities({
              sort_by: "city_name",
              sort_order: "asc",
            })) as {
              cities: City[];
            };
            set({
              cities: response.cities,
              totalCount: response.cities.length,
              currentPage: 1,
              totalPages: 1,
              itemsPerPage: response.cities.length || 1,
              hasLoaded: true,
              lastFetchedAt: new Date().toISOString(),
              loading: false,
              isRefreshing: false,
            });
          } catch (error) {
            console.error("Error refreshing cities:", error);
            set({ loading: false, isRefreshing: false });
          }
        },
      }),
      {
        name: "city-store",
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          cities: state.cities,
          hasLoaded: state.hasLoaded,
          lastFetchedAt: state.lastFetchedAt,
          totalCount: state.totalCount,
        }),
      },
    ),
    { name: "city-store-devtools" },
  ),
);
